import { useCallback } from 'react';
import { Participant } from 'twilio-video';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useAppSettings, useDialog, useMutedParticipants, useSelectedParticipant, useSignalrConnection } from 'hooks';
import { ParticipantDotMenuContext } from './context';
import { ISignalrConnectionContext } from 'context/signalrConnection';
import { TwoOptionsDialog } from 'components';
import { parseParticipantIdentity } from 'utils';
import { LayoutType } from 'types';
import { IMutedParticipantsContext } from 'context/mutedParticipants';

import { RequeueDialog } from 'components/Dialogs/RequeueDialog';

interface ParticipantDotMenuProviderProps {
  children?: JSX.Element;
}

export enum ParticipantDotMenuDialogType {
  RENAME,
  SPOTLIGHT,
  REQUEUE,
  REMOVE,
  MUTE,
}

export function ParticipantDotMenuProvider({ children }: ParticipantDotMenuProviderProps) {
  const { t } = useTranslation('providerRoom');
  const { showDialog } = useDialog();
  const { removeParticipantFromCall }: ISignalrConnectionContext = useSignalrConnection();
  const [selectedParticipant, setSelectedParticipant] = useSelectedParticipant();
  const { isGalleryViewActive, setLayoutType } = useAppSettings();
  const { mutedParticipantsId, muteParticipant }: IMutedParticipantsContext = useMutedParticipants();

  const onDialogOpen = useCallback(
    (type: ParticipantDotMenuDialogType, participant: Participant) => {
      switch (type) {
        case ParticipantDotMenuDialogType.RENAME:
          break;

        case ParticipantDotMenuDialogType.MUTE: {
          const [id] = parseParticipantIdentity(participant);
          const isMuted: boolean = mutedParticipantsId.some((mutedParticipantId: string) => mutedParticipantId === id);

          muteParticipant(id, !isMuted);

          break;
        }

        case ParticipantDotMenuDialogType.SPOTLIGHT: {
          const [, identity] = parseParticipantIdentity(participant);
          const unspotlight: boolean = participant === selectedParticipant;
          const modal = showDialog(TwoOptionsDialog, {
            title: unspotlight ? t('unspotlight') : t('spotlight'),
            text: (
              <Typography variant='body2' component='p'>
                {unspotlight
                  ? t('unspotlightIdentityQuestion', { identity })
                  : t('spotlightIdentityQuestion', { identity })}
              </Typography>
            ),
            confirmText: t('yes'),
            onConfirm: () => {
              modal.destroy();
              setSelectedParticipant(unspotlight ? null : participant);

              if (isGalleryViewActive) {
                setLayoutType(LayoutType.SPEAKER);
              }
            },
          });
          break;
        }

        case ParticipantDotMenuDialogType.REQUEUE: {
          const [id, identity] = parseParticipantIdentity(participant);

          const hasCameraAccess =
            typeof participant.videoTracks === 'object' &&
            Array.from(participant.videoTracks.values()).some((pub) => pub.track !== null);

          const hasMicroAccess =
            typeof participant.audioTracks === 'object' &&
            Array.from(participant.audioTracks.values()).some((pub) => pub.track !== null);

          const modal = showDialog(RequeueDialog, {
            patient: {
              connectionId: id,
              userName: identity,
              phoneNumber: '',
              joinedOn: new Date().toISOString(),
              callAccesses: {
                hasCameraAccess,
                hasMicroAccess,
              },
            },
            onClose: () => {
              modal.destroy();
            },
          });

          break;
        }

        case ParticipantDotMenuDialogType.REMOVE: {
          const [id, identity] = parseParticipantIdentity(participant);
          const modal = showDialog(TwoOptionsDialog, {
            title: t('removeUpper'),
            text: (
              <Typography variant='body2' component='p'>
                {t('removeIdentityFromCallQuestion', { identity })}
              </Typography>
            ),
            confirmText: t('remove'),
            onConfirm: () => {
              modal.destroy();
              removeParticipantFromCall(id);
            },
          });
          break;
        }
        default:
          break;
      }
    },
    [
      isGalleryViewActive,
      mutedParticipantsId,
      removeParticipantFromCall,
      selectedParticipant,
      setLayoutType,
      setSelectedParticipant,
      showDialog,
      t,
      muteParticipant,
    ],
  );

  return (
    <ParticipantDotMenuContext.Provider
      value={{
        onDialogOpen,
      }}
    >
      {children}
    </ParticipantDotMenuContext.Provider>
  );
}
